/**
 * Utilidades para drag and drop con @dnd-kit
 * Estas funciones proporcionan operaciones comunes para manipular listas
 */

/**
 * Reordena items dentro de una lista
 * @param {Array} list - Lista original
 * @param {number} startIndex - Índice origen
 * @param {number} endIndex - Índice destino
 * @returns {Array} Lista reordenada
 */
export function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

/**
 * Mueve un item entre dos listas
 * @param {Array} source - Lista origen
 * @param {Array} destination - Lista destino
 * @param {number} sourceIndex - Índice en lista origen
 * @param {number} destinationIndex - Índice en lista destino
 * @returns {Object} { source: newSource, destination: newDest }
 */
export function moveBetweenLists(source, destination, sourceIndex, destinationIndex) {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(sourceIndex, 1);
  destClone.splice(destinationIndex, 0, removed);

  return {
    source: sourceClone,
    destination: destClone,
  };
}

/**
 * Encuentra el índice de un item en una lista por su ID
 * @param {Array} items - Lista de items
 * @param {string|number} id - ID a buscar
 * @param {string} idKey - Key del ID en cada item (default: 'id')
 * @returns {number} Índice del item o -1 si no se encuentra
 */
export function findItemIndex(items, id, idKey = 'id') {
  return items.findIndex((item) => {
    const itemId = item[idKey];
    return String(itemId) === String(id);
  });
}

/**
 * Normaliza el resultado de @dnd-kit al formato esperado por los hooks
 * @param {Object} event - Evento de @dnd-kit (onDragEnd)
 * @param {Array} items - Lista de items actual
 * @param {string} droppableId - ID del droppable
 * @param {string} idKey - Key del ID en cada item
 * @returns {Object|null} Resultado normalizado o null si no hay destino válido
 */
export function normalizeResult(event, items, droppableId, idKey = 'id') {
  const { active, over } = event;

  if (!over) {
    return null;
  }

  const oldIndex = findItemIndex(items, active.id, idKey);
  const newIndex = findItemIndex(items, over.id, idKey);

  if (oldIndex === -1) {
    return null;
  }

  return {
    source: {
      droppableId,
      index: oldIndex,
    },
    destination: {
      droppableId: over.data?.current?.sortable?.containerId || droppableId,
      index: newIndex !== -1 ? newIndex : items.length,
    },
    draggableId: String(active.id),
  };
}

/**
 * Encuentra un item en el árbol de forma recursiva
 * @param {Array} items - Árbol de items
 * @param {string|number} itemId - ID del item a buscar
 * @param {string} childrenKey - Key de los hijos (default: 'childs')
 * @returns {Object|null} Item encontrado o null
 */
export function findItemDeep(items, itemId, childrenKey = 'childs') {
  for (const item of items) {
    if (item.id === itemId) {
      return item;
    }
    if (item[childrenKey]) {
      const found = findItemDeep(item[childrenKey], itemId, childrenKey);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Remueve un item del árbol de forma recursiva
 * @param {Array} items - Árbol de items
 * @param {string|number} itemId - ID del item a remover
 * @param {string} childrenKey - Key de los hijos (default: 'childs')
 * @returns {Array} Nuevo árbol sin el item
 */
export function removeItemDeep(items, itemId, childrenKey = 'childs') {
  return items
    .filter((item) => item.id !== itemId)
    .map((item) => {
      if (item[childrenKey]) {
        return {
          ...item,
          [childrenKey]: removeItemDeep(item[childrenKey], itemId, childrenKey),
        };
      }
      return item;
    });
}

/**
 * Agrega un item como hijo de otro en el árbol
 * @param {Array} items - Árbol de items
 * @param {string|number} parentId - ID del padre
 * @param {Object} newItem - Item a agregar
 * @param {string} childrenKey - Key de los hijos (default: 'childs')
 * @returns {Array} Nuevo árbol con el item agregado
 */
export function addAsChild(items, parentId, newItem, childrenKey = 'childs') {
  return items.map((item) => {
    if (item.id === parentId) {
      return {
        ...item,
        [childrenKey]: [...(item[childrenKey] || []), newItem],
      };
    }
    if (item[childrenKey]) {
      return {
        ...item,
        [childrenKey]: addAsChild(item[childrenKey], parentId, newItem, childrenKey),
      };
    }
    return item;
  });
}

/**
 * Agrega un item como hermano (antes o después) de otro en el árbol
 * @param {Array} items - Árbol de items
 * @param {string|number} targetId - ID del item objetivo
 * @param {Object} newItem - Item a agregar
 * @param {boolean} insertAfter - Si true, inserta después; si false, antes
 * @param {string} childrenKey - Key de los hijos (default: 'childs')
 * @returns {Array} Nuevo árbol con el item agregado
 */
export function addAsSibling(items, targetId, newItem, insertAfter = true, childrenKey = 'childs') {
  const result = [];
  for (const item of items) {
    if (item.id === targetId) {
      if (insertAfter) {
        result.push(item);
        result.push(newItem);
      } else {
        result.push(newItem);
        result.push(item);
      }
    } else {
      if (item[childrenKey]) {
        result.push({
          ...item,
          [childrenKey]: addAsSibling(item[childrenKey], targetId, newItem, insertAfter, childrenKey),
        });
      } else {
        result.push(item);
      }
    }
  }
  return result;
}

/**
 * Aplana un árbol jerárquico a una lista con información de parentId y depth
 * @param {Array} items - Árbol de items
 * @param {string|null} parentId - ID del padre actual
 * @param {number} depth - Profundidad actual
 * @param {string} childrenKey - Key de los hijos (default: 'childs')
 * @returns {Array} Lista aplanada
 */
export function flattenTree(items, parentId = null, depth = 0, childrenKey = 'childs') {
  return items.reduce((acc, item) => {
    const flatItem = { ...item, parentId, depth };
    acc.push(flatItem);
    if (item[childrenKey] && item[childrenKey].length > 0) {
      acc.push(...flattenTree(item[childrenKey], item.id, depth + 1, childrenKey));
    }
    return acc;
  }, []);
}
